/**
 * Tabela atrybutow warstwy - dok pod mapa.
 *
 * Dlaczego z serwera, a nie z tego, co juz jest w przegladarce: warstwa niesie
 * atrybuty wszystkich swoich obiektow, a przegladarka ma tylko wypieczone
 * instrukcje dla biezacego kadru. Tabela pokazujaca "to, co akurat widac"
 * bylaby czyms innym niz tabela atrybutow - i nie dalo by sie z niej dojsc do
 * obiektu lezacego poza ekranem, czyli do tego, po co sie ja otwiera.
 *
 * Wiersze ida stronami po ROWS_PAGE, bo warstwa powiatowa ma ich setki tysiecy.
 */
import { api, type LayerInfo, type ReportInfo, type TableRow } from './api'
import { esc, nf, plural, OBIEKT, store, restore } from './util'

/** Polskie liczebniki dla ostrzezenia o brakujacych warstwach. */
const WARSTWA = { one: 'warstwy', few: 'warstw', many: 'warstw', other: 'warstw' }

const PAGE = 200
const LAYER_KEY = 'gmlweb.tblLayer'

/** Obiekt punktowy ma obwiednie o zerowym boku - przyblizenie dostaje ten bufor. */
const POINT_PAD_M = 12

/** Ikony rysowane obrysem, tak jak reszta interfejsu; barwa z currentColor. */
const ICON_ZOOM = `<svg viewBox="0 0 16 16" aria-hidden="true">
  <circle cx="8" cy="8" r="3.1"/><path d="M8 1.7v2.3M8 12v2.3M1.7 8H4M12 8h2.3"/></svg>`
const ICON_CLOSE = `<svg viewBox="0 0 16 16" aria-hidden="true">
  <path d="M4.4 4.4 11.6 11.6M11.6 4.4 4.4 11.6"/></svg>`
const ICON_DOC = `<svg viewBox="0 0 16 16" aria-hidden="true">
  <path d="M3.6 2.4h5.8l2.9 2.9v8.3H3.6z"/><path d="M9.4 2.4v3h3"/>
  <path d="M5.8 8.2h4.4M5.8 10.6h4.4"/></svg>`
const ICON_CSV = `<svg viewBox="0 0 16 16" aria-hidden="true">
  <path d="M8 2.6v7.4m0 0L5.2 7.2M8 10l2.8-2.8"/>
  <path d="M2.8 11.6v1.1a1.2 1.2 0 0 0 1.2 1.2h8a1.2 1.2 0 0 0 1.2-1.2v-1.1"/></svg>`

/** Pozycja wybieraka: zestawienie albo klasa. */
interface Pick {
  kind: 'layer' | 'report'
  key: string
  label: string
  count: number | null
}

/** Podswietlenie trafionego fragmentu nazwy na liscie klas. */
function mark(name: string, needle: string): string {
  const i = needle ? name.toLowerCase().indexOf(needle) : -1
  if (i < 0) return esc(name)
  return esc(name.slice(0, i)) + '<b>' + esc(name.slice(i, i + needle.length)) + '</b>'
       + esc(name.slice(i + needle.length))
}

export interface TableHost {
  projectId: string
  layers: LayerInfo[]
  /** Zasieg widoku mapy - dla filtra "tylko w widoku". */
  viewExtent(): number[]
  /** Wiersz wskazany: zaznaczenie na mapie i atrybuty w panelu obok. */
  onPick(id: number, layer: string): void
  /** Przyblizenie mapy do obiektu. */
  onZoom(ext: [number, number, number, number]): void
  /** Zmiana wysokosci doku - mapa musi sie przeliczyc. */
  onResize(): void
}

export class AttrTable {
  private el: HTMLElement
  private host: TableHost

  private layer = ''
  /** Wybrane zestawienie; null znaczy "pokazujemy zwykla tabele warstwy". */
  private report: ReportInfo | null = null
  private reports: ReportInfo[] = []
  private missing: string[] = []
  private fields: string[] = []
  private rows: TableRow[] = []
  private total = 0
  private selected: number | null = null
  private onlyView = false
  private picks: Pick[] = []        // pozycje pasujace do tego, co wpisane
  private pickIdx = 0
  private pickQ = ''            // fragment, ktorym zawezono liste
  private pickOpen = false
  private busy = false
  private seq = 0                  // odsiewa odpowiedzi po zmianie zrodla/filtra
  private timer: number | undefined
  private ready: Promise<void>

  constructor(el: HTMLElement, host: TableHost) {
    this.el = el
    this.host = host

    const stored = restore(LAYER_KEY)
    this.layer = host.layers.some(l => l.name === stored) ? stored!
               : (host.layers[0]?.name ?? '')

    this.el.innerHTML = this.chromeHTML()
    this.wireChrome()

    // Zestawienia dociagamy osobno i bez czekania: lista warstw wystarcza,
    // zeby dok byl uzyteczny, a ktore raporty maja sens, wie dopiero serwer.
    this.ready = api.reports(host.projectId)
      .then(r => { this.reports = r.filter(x => x.ready) })
      .catch(e => { console.error(e) })
  }

  /** Czy projekt ma jakiekolwiek zestawienie - do guzika w belce narzedzi. */
  async hasReports(): Promise<boolean> {
    await this.ready
    return this.reports.length > 0
  }

  /**
   * Otwarcie doku wprost na zestawieniu.
   *
   * Osobne wejscie, bo w samym wybieraku zestawienia sa nie do znalezienia:
   * trzeba wiedziec, ze siedza pod polem wyboru klasy.
   */
  async showReports() {
    await this.ready
    if (!this.reports.length) return
    // Zestawienie wybieramy PRZED otwarciem doku - inaczej pierwsze wczytanie
    // poszloby po dane domyslnej klasy, ktore i tak zaraz zastapimy.
    if (!this.report) {
      this.applyPick({ kind: 'report', key: this.reports[0].key,
                       label: this.reports[0].title, count: null })
    }
    this.toggle(true)
    this.input().focus()
  }

  /** Powrot z zestawienia do zwyklej tabeli atrybutow. */
  showTable() {
    if (this.report) {
      this.applyPick({ kind: 'layer', key: this.layer, label: this.layer, count: null })
      this.toggle(true)
      return
    }
    this.toggle()
  }

  /** Co teraz pokazuje dok - do napisu w naglowku i do nazwy pliku CSV. */
  private src(): { kind: 'layer' | 'report'; key: string } {
    return this.report ? { kind: 'report', key: this.report.key }
                       : { kind: 'layer', key: this.layer }
  }

  get open(): boolean { return !this.el.hidden }

  toggle(on = !this.open) {
    this.el.hidden = !on
    this.host.onResize()
    if (on && !this.rows.length) this.load()
  }

  /** Mapa sie przesunela - przy filtrze "tylko w widoku" tabela idzie za nia. */
  viewChanged() {
    if (!this.open || !this.onlyView) return
    window.clearTimeout(this.timer)
    this.timer = window.setTimeout(() => this.load(), 250)
  }

  /** Podswietlenie wiersza obiektu wskazanego na mapie. */
  markSelected(id: number | null) {
    this.selected = id
    if (!this.open) return
    let row: HTMLElement | null = null
    this.el.querySelectorAll<HTMLElement>('tbody tr').forEach(tr => {
      const on = Number(tr.dataset.id) === id
      tr.classList.toggle('on', on)
      tr.setAttribute('aria-selected', String(on))
      if (on) row = tr
    })
    ;(row as HTMLElement | null)?.scrollIntoView({ block: 'nearest' })
  }

  // --------------------------------------------------------------- dane

  private async load(more = false) {
    if (!this.layer && !this.report) return
    // Blokada dotyczy TYLKO doczytywania kolejnej strony. Gdyby obejmowala
    // takze zmiane zrodla, przelaczenie na zestawienie tuz po otwarciu doku
    // przepadaloby bez sladu - odpowiedz dla poprzedniego zrodla jest juz w
    // drodze, a nowe zadanie odbijaloby sie od 'busy'. Spoznione odpowiedzi
    // odsiewa licznik seq.
    if (more && this.busy) return
    this.busy = true
    const seq = ++this.seq
    const offset = more ? this.rows.length : 0
    if (!more) { this.rows = []; this.fields = [] }
    this.drawBody()

    const opts = {
      limit: PAGE, offset,
      bbox: this.onlyView ? this.host.viewExtent() : undefined,
    }
    let page: { fields: string[]; rows: TableRow[]; total: number } | null = null
    try {
      // Zestawienie i tabela warstwy oddaja wiersze w tym samym ksztalcie
      // (id, obwiednia, wartosci), wiec dalej idzie jedna droga.
      page = this.report
        ? await api.report(this.host.projectId, this.report.key, opts)
            .then(p => { this.missing = p.missing; return { ...p, fields: p.columns } })
        : await api.rows(this.host.projectId, this.layer, opts)
    } catch (e) {
      console.error(e)
    }
    this.busy = false
    if (seq !== this.seq) return    // w miedzyczasie zmieniono zrodlo albo filtr

    if (page) {
      this.fields = page.fields
      this.rows = more ? [...this.rows, ...page.rows] : page.rows
      this.total = page.total
    }
    this.drawCount()
    this.drawKind()
    this.drawBody(!page)
  }

  // -------------------------------------------------------------- widok

  private chromeHTML(): string {
    return `
      <div class="grip row" role="separator" aria-orientation="horizontal" tabindex="0"
           title="Przeciągnij, aby zmienić wysokość (dwuklik przywraca domyślną)"
           aria-label="Wysokość tabeli atrybutów"></div>
      <div class="tbl-top">
        <span class="tbl-lbl" id="tbl-kind">Tabela atrybutów</span>
        <select id="tbl-rep" class="tbl-rep" aria-label="Zestawienie" hidden></select>
        <div class="tbl-pick" id="tbl-pick">
          <input id="tbl-lyr" class="tbl-inp" type="text" role="combobox"
                 autocomplete="off" spellcheck="false" aria-autocomplete="list"
                 aria-expanded="false" aria-controls="tbl-lyr-list" aria-label="Klasa obiektów albo zestawienie"
                 placeholder="wpisz nazwę klasy lub zestawienia…" value="${esc(this.layer)}">
          <div class="tbl-opts" id="tbl-lyr-list" role="listbox" hidden></div>
        </div>
        <span class="tbl-ct mono"></span>
        <label class="switch" title="Pokaż tylko obiekty widoczne w kadrze mapy">
          <input type="checkbox" id="tbl-view">
          <span class="track" aria-hidden="true"></span>
          <span>tylko w widoku</span>
        </label>
        <span class="tbl-warn" id="tbl-warn" hidden></span>
        <span class="spacer"></span>
        <a class="icon-btn sm" id="tbl-csv" download
           title="Pobierz jako CSV" aria-label="Pobierz jako plik CSV">${ICON_CSV}</a>
        <button class="icon-btn sm" id="tbl-close" type="button"
                title="Zamknij tabelę" aria-label="Zamknij tabelę atrybutów">${ICON_CLOSE}</button>
      </div>
      <div class="tbl-body" id="tbl-body"></div>`
  }

  private wireChrome() {
    const inp = this.input()

    inp.onfocus = () => { inp.select(); this.openPicker() }
    inp.oninput = () => this.openPicker()
    inp.onblur = () => { this.closePicker(); inp.value = this.layer }

    inp.onkeydown = e => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        if (!this.pickOpen) return this.openPicker()
        const d = e.key === 'ArrowDown' ? 1 : -1
        this.pickIdx = (this.pickIdx + d + this.picks.length) % this.picks.length
        this.drawPicker()
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const hit = this.picks[this.pickIdx]
        if (hit) this.applyPick(hit)
      } else if (e.key === 'Escape') {
        // Bez tego Escape doleci do mapy i przy okazji zdejmie zaznaczenie
        // obiektu - a chodzi tylko o zamkniecie listy.
        e.stopPropagation()
        this.closePicker()
        inp.value = this.layer
      }
    }

    // pointerdown, nie click: blur pola leci wczesniej niz click i zdazylby
    // zamknac liste, zanim klikniecie do niej dotrze.
    this.el.querySelector<HTMLElement>('#tbl-lyr-list')!.onpointerdown = e => {
      const opt = (e.target as HTMLElement).closest<HTMLElement>('.tbl-opt[data-k]')
      if (!opt) return
      e.preventDefault()
      const hit = this.picks[Number(opt.dataset.i)]
      if (hit) this.applyPick(hit)
    }

    this.el.querySelector<HTMLSelectElement>('#tbl-rep')!.onchange = e => {
      const key = (e.target as HTMLSelectElement).value
      const r = this.reports.find(x => x.key === key)
      if (r) this.applyPick({ kind: 'report', key: r.key, label: r.title, count: null })
    }

    this.el.querySelector<HTMLInputElement>('#tbl-view')!.onchange = e => {
      this.onlyView = (e.target as HTMLInputElement).checked
      this.load()
    }
    this.el.querySelector<HTMLButtonElement>('#tbl-close')!.onclick = () => this.toggle(false)
  }

  // ------------------------------------------------------ wybor warstwy

  private input(): HTMLInputElement {
    return this.el.querySelector<HTMLInputElement>('#tbl-lyr')!
  }

  /**
   * Zestawienia i klasy w jednej liscie, zawezanej tym, co wpisane w polu.
   *
   * Filtr szuka FRAGMENTU, nie poczatku nazwy: klasy zaczynaja sie od prefiksu
   * zbioru (EGB_, GES_, OT_), wiec szukajac dzialek nikt nie zaczyna pisac od
   * "EGB_". Przy pustym polu lista pokazuje wszystko, jak zwykla rozwijana.
   */
  private openPicker() {
    // Zawartosc rowna biezacemu zrodlu znaczy "jeszcze nic nie wpisano" -
    // inaczej samo wejscie w pole pokazywaloby liste z jedna pozycja, bo nazwa
    // filtrowalaby sama siebie.
    const raw = this.input().value.trim()
    const q = this.pickQ = raw === this.layer ? '' : raw.toLowerCase()

    const all: Pick[] = this.host.layers.map(l => ({
      kind: 'layer' as const, key: l.name, label: l.name, count: l.feature_count }))
    this.picks = q ? all.filter(p => p.label.toLowerCase().includes(q)) : all

    const cur = this.src()
    this.pickIdx = Math.max(0, this.picks.findIndex(
      p => p.kind === cur.kind && p.key === cur.key))
    this.pickOpen = true
    this.drawPicker()
  }

  private closePicker() {
    if (!this.pickOpen) return
    this.pickOpen = false
    this.drawPicker()
  }

  private drawPicker() {
    const list = this.el.querySelector<HTMLElement>('#tbl-lyr-list')!
    const inp = this.input()
    list.hidden = !this.pickOpen
    inp.setAttribute('aria-expanded', String(this.pickOpen))
    if (!this.pickOpen) {
      inp.removeAttribute('aria-activedescendant')
      return
    }

    if (!this.picks.length) {
      list.innerHTML = '<div class="tbl-opt none">Nic nie pasuje.</div>'
      inp.removeAttribute('aria-activedescendant')
      return
    }

    list.innerHTML = this.picks.map((p, i) => {
      return `
      <div class="tbl-opt${i === this.pickIdx ? ' on' : ''}" role="option"
           id="tbl-opt-${i}" aria-selected="${i === this.pickIdx}"
           data-k="${p.kind}" data-i="${i}">
        <span class="o-nm">${mark(p.label, this.pickQ)}</span>
        <span class="o-ct mono">${p.count === null ? '' : nf.format(p.count)}</span>
      </div>`
    }).join('')
    inp.setAttribute('aria-activedescendant', `tbl-opt-${this.pickIdx}`)
    list.querySelector('.tbl-opt.on')?.scrollIntoView({ block: 'nearest' })
  }

  private applyPick(p: Pick) {
    const cur = this.src()
    this.closePicker()
    if (p.kind === 'report') {
      this.report = this.reports.find(r => r.key === p.key) ?? null
    } else {
      this.report = null
      this.layer = p.key
      store(LAYER_KEY, p.key)
    }
    if (p.kind === 'layer') this.input().value = p.key
    this.drawKind()
    if (p.kind === cur.kind && p.key === cur.key) return
    this.missing = []
    this.load()
  }

  /** Naglowek doku i odnosnik CSV ida za wybranym zrodlem. */
  private drawKind() {
    const rep = !!this.report
    const kind = this.el.querySelector<HTMLElement>('#tbl-kind')
    if (kind) {
      kind.textContent = rep ? 'Zestawienie EGiB' : 'Tabela atrybutów'
      kind.classList.toggle('rep', rep)
    }
    // Tryb widac po sterowaniu, nie po samym napisie: zestawienie ma wlasna
    // liste, tabela - pole z filtrowaniem klas. Jedno wspolne sterowanie
    // sprawialo, ze guzik zestawien wygladal, jakby otwieral tabele.
    const pick = this.el.querySelector<HTMLElement>('#tbl-pick')
    const sel = this.el.querySelector<HTMLSelectElement>('#tbl-rep')
    if (pick) pick.hidden = rep
    if (sel) {
      sel.hidden = !rep
      if (rep && sel.options.length !== this.reports.length) {
        sel.innerHTML = this.reports.map(r =>
          `<option value="${esc(r.key)}">${esc(r.title)}</option>`).join('')
      }
      if (rep) sel.value = this.report!.key
    }

    const csv = this.el.querySelector<HTMLAnchorElement>('#tbl-csv')
    if (csv) {
      csv.href = api.csvUrl(this.host.projectId, this.src(),
                            this.onlyView ? this.host.viewExtent() : undefined)
    }

    const warn = this.el.querySelector<HTMLElement>('#tbl-warn')
    if (warn) {
      // Zestawienie liczy sie takze bez czesci warstw - tyle ze niektore
      // kolumny zostaja puste. Lepiej to powiedziec, niz zostawic mysleniu.
      warn.hidden = !this.missing.length
      warn.textContent = this.missing.length
        ? `bez ${plural(this.missing.length, WARSTWA)} — część kolumn zostanie pusta`
        : ''
      warn.title = this.missing.join(', ')
    }
  }

  // -------------------------------------------------------------- tabela

  private drawCount() {
    const box = this.el.querySelector<HTMLElement>('.tbl-ct')
    if (!box) return
    const shown = this.rows.length
    box.textContent = shown < this.total
      ? `${nf.format(shown)} z ${plural(this.total, OBIEKT)}`
      : plural(this.total, OBIEKT)
  }

  private drawBody(failed = false) {
    const box = this.el.querySelector<HTMLElement>('#tbl-body')!

    if (!this.rows.length) {
      box.innerHTML = `<div class="tbl-ph">${
        this.busy ? 'Wczytywanie…'
        : failed ? 'Nie udało się wczytać tabeli.'
        : this.onlyView ? 'Brak obiektów tej warstwy w widoku mapy.'
        : 'Warstwa nie ma obiektów.'}</div>`
      return
    }

    // Kolumna z guzikiem lupy stoi pierwsza i jest przyklejona, zeby przy
    // szerokiej tabeli nie uciekala w bok razem z reszta.
    const head = `<tr><th class="c-zoom"></th>${
      this.report?.doc ? '<th class="c-doc"></th>' : ''}<th class="c-nr">#</th>${
      this.fields.map(f => `<th title="${esc(f)}">${esc(f)}</th>`).join('')}</tr>`

    const body = this.rows.map((r, i) => `
      <tr data-id="${r.id}" data-i="${i}" role="row"
          class="${r.id === this.selected ? 'on' : ''}"
          aria-selected="${r.id === this.selected}">
        <td class="c-zoom">${r.ext
          ? `<button class="zoom" type="button" data-i="${i}"
                     title="Przybliż do obiektu"
                     aria-label="Przybliż do obiektu">${ICON_ZOOM}</button>`
          : ''}</td>
        ${this.report?.doc ? `<td class="c-doc"><button class="docbtn" type="button"
             data-i="${i}" title="Otwórz wypis z rejestru gruntów"
             aria-label="Otwórz wypis">${ICON_DOC}</button></td>` : ''}
        <td class="c-nr mono">${nf.format(i + 1)}</td>
        ${this.fields.map((_, c) => {
          const v = r.values[c]
          return `<td class="mono" title="${esc(v ?? '')}">${v == null ? '' : esc(v)}</td>`
        }).join('')}
      </tr>`).join('')

    const more = this.rows.length < this.total
      ? `<div class="tbl-more"><button type="button" id="tbl-more"${this.busy ? ' disabled' : ''}
           >${this.busy ? 'Wczytywanie…' : `Wczytaj kolejne ${nf.format(
             Math.min(PAGE, this.total - this.rows.length))}`}</button></div>`
      : ''

    box.innerHTML = `<table class="tbl-t"><thead>${head}</thead><tbody>${body}</tbody></table>${more}`
    this.wireRows()
  }

  private wireRows() {
    const box = this.el.querySelector<HTMLElement>('#tbl-body')!

    box.querySelectorAll<HTMLTableRowElement>('tbody tr').forEach(tr => {
      const i = Number(tr.dataset.i)
      // Klikniecie wybiera, dwuklik przybliza - tak jak w tabeli QGIS-a.
      // Samo przewijanie listy nie ma szarpac mapa.
      tr.onclick = () => this.pick(i)
      tr.ondblclick = () => this.zoom(i)
    })

    box.querySelectorAll<HTMLButtonElement>('button.zoom').forEach(b => {
      b.onclick = e => {
        e.stopPropagation()          // guzik lupy nie ma dodatkowo wybierac wiersza
        this.pick(Number(b.dataset.i))
        this.zoom(Number(b.dataset.i))
      }
    })

    box.querySelectorAll<HTMLButtonElement>('button.docbtn').forEach(b => {
      b.onclick = e => {
        e.stopPropagation()
        const r = this.rows[Number(b.dataset.i)]
        if (r) { this.pick(Number(b.dataset.i)); this.openDoc(r.id) }
      }
    })

    const more = box.querySelector<HTMLButtonElement>('#tbl-more')
    if (more) more.onclick = () => this.load(true)
  }

  /**
   * Wypis w oknie nad mapa. Dokument idzie w ramce, a nie wprost do strony:
   * ma wlasne style pod wydruk A4 i bez izolacji rozjechalby interfejs.
   */
  private openDoc(featureId: number) {
    const url = api.docUrl(this.host.projectId, featureId)
    const plik = api.docUrl(this.host.projectId, featureId, true)
    document.getElementById('doc-wrap')?.remove()
    const box = document.createElement('div')
    box.id = 'doc-wrap'
    box.className = 'doc-wrap'
    box.innerHTML = `
      <div class="doc-box" role="dialog" aria-modal="true" aria-label="Wypis z rejestru gruntów">
        <div class="doc-top">
          <b>Wypis z rejestru gruntów</b>
          <span class="spacer"></span>
          <a class="icon-btn sm" href="${plik}" download
             title="Pobierz wypis jako plik">${ICON_CSV}</a>
          <button class="icon-btn sm" data-close type="button"
                  title="Zamknij">${ICON_CLOSE}</button>
        </div>
        <iframe src="${url}" title="Wypis z rejestru gruntów"></iframe>
      </div>`
    document.body.appendChild(box)
    const close = () => box.remove()
    box.querySelector('[data-close]')!.addEventListener('click', close)
    box.addEventListener('click', e => { if (e.target === box) close() })
    window.addEventListener('keydown', function esc(e) {
      if (e.key === 'Escape') { close(); window.removeEventListener('keydown', esc) }
    })
  }

  private pick(i: number) {
    const r = this.rows[i]
    if (!r) return
    this.markSelected(r.id)
    this.host.onPick(r.id, this.layer)
  }

  private zoom(i: number) {
    const r = this.rows[i]
    if (!r?.ext) return
    const [x0, y0, x1, y1] = r.ext
    // Obiekt punktowy (i linia rownolegla do osi) ma bok obwiedni rowny zeru -
    // fit() dostalby zdegenerowany zasieg i przeskoczyl na maksymalne zblizenie.
    const px = Math.max(POINT_PAD_M - (x1 - x0) / 2, 0)
    const py = Math.max(POINT_PAD_M - (y1 - y0) / 2, 0)
    this.host.onZoom([x0 - px, y0 - py, x1 + px, y1 + py])
  }
}
